import { createFileRoute, Link } from '@tanstack/react-router'
import { useAuth } from '@/lib/auth-context'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@lead-magnifier/ui'
import { Users, Calendar, TrendingUp, Check } from 'lucide-react'
import { useLeadStats } from '@/hooks/use-leads'
import { useEvents } from '@/hooks/use-events'

export const Route = createFileRoute('/_authenticated/dashboard')({
  component: DashboardPage,
})

function DashboardPage() {
  const { user } = useAuth()
  const { data: leadStats, isLoading: leadsLoading } = useLeadStats()
  const { data: events, isLoading: eventsLoading } = useEvents()

  const activeEvents = events?.filter((e) => e.status === 'active').length || 0
  const isLoading = leadsLoading || eventsLoading

  const stats = [
    {
      title: 'Gesamte Leads',
      value: leadStats?.total?.toString() || '0',
      description: 'Seit Beginn',
      icon: Users,
    },
    {
      title: 'Aktive Events',
      value: activeEvents.toString(),
      description: 'Momentan aktiv',
      icon: Calendar,
    },
    {
      title: 'Conversion Rate',
      value: `${leadStats?.conversionRate || 0}%`,
      description: 'DOI bestätigt',
      icon: TrendingUp,
    },
    {
      title: 'Bestätigte Leads',
      value: leadStats?.confirmed?.toString() || '0',
      description: 'Double Opt-In',
      icon: Check,
    },
  ]

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold">
          Willkommen zurück{user?.user_metadata?.name ? `, ${user.user_metadata.name}` : ''}!
        </h1>
        <p className="text-muted-foreground mt-2">
          Hier ist ein Überblick über deine Lead-Performance.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
              <stat.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <div className="text-2xl font-bold">{stat.value}</div>
              )}
              <p className="text-xs text-muted-foreground">{stat.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Letzte Leads</CardTitle>
            <CardDescription>Die neuesten erfassten Kontakte</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ) : (leadStats?.total || 0) === 0 ? (
              <p className="text-sm text-muted-foreground">
                Noch keine Leads vorhanden. Erstelle dein erstes Event, um Leads zu sammeln.
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  {leadStats?.total} Leads gesammelt, {leadStats?.confirmed} bestätigt
                </p>
                <Link to="/leads" className="text-sm text-primary hover:underline">
                  Alle Leads ansehen →
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Häufige Aktionen</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Link
              to="/events/new"
              className="block rounded-md border p-3 hover:bg-muted transition-colors"
            >
              <div className="font-medium">Neues Event erstellen</div>
              <div className="text-sm text-muted-foreground">
                Starte ein neues Lead-Formular
              </div>
            </Link>
            <Link
              to="/leads"
              className="block rounded-md border p-3 hover:bg-muted transition-colors"
            >
              <div className="font-medium">Leads exportieren</div>
              <div className="text-sm text-muted-foreground">
                CSV Export für alle Events
              </div>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
